import history from '../history';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AsyncDictionaryRequest, DictionaryUpload } from './action-types';
import {
  showFeedback,
  dismissFeedback,
  showFeedbackError
} from './feedback.actions';
import { loading } from './ui-core.actions';
import { api } from '../core/net';

import { HttpMethods, cmxErrors, commonEnums } from '@codametrix/ui-common';
import { loadDictionary } from './dictionary';
import { toggleDeleteModal } from './dictionary-values';
import { isNewDesign } from '../core/middleware/new-design-middleware';

const { BadRequestError } = cmxErrors;

type RequestStatus = {
  requestIds: number[];
  successRoute: string;
};

const requestInitiated = createAction<RequestStatus>(
  AsyncDictionaryRequest.ASYNC_REQUEST_INIT
);

const requestUpdate = createAction<RequestStatus>(
  AsyncDictionaryRequest.ASYNC_REQUEST_PROGRESS
);

const selectedDictionary = createAction<{ dictionaryId: number }>(
  AsyncDictionaryRequest.SELECT_DICTIONARY
);

const fetchRequestStatus = createAsyncThunk<
  CMxAPI.DictionaryRequestResponse,
  RequestStatus,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  AsyncDictionaryRequest.FETCH_REQUEST_STATUS,
  async (requestInfo, { dispatch }) => {
    const { requestIds, successRoute } = requestInfo;
    const responseList: CMxAPI.DictionaryRequestResponse[] = await Promise.all(
      requestIds.map(requestId => {
        const endpoint = `/dictionary/request/${requestId}/v2`;
        isNewDesign && dispatch(loading(true) as any);
        const uploadProcessing = api<CMxAPI.DictionaryRequestResponse>(
          { endpoint },
          false
        ).finally(() => {
          isNewDesign && dispatch(loading(false) as any);
        });
        return uploadProcessing;
      })
    );

    let requestType = responseList[0].requestType;
    if (requestType)
      requestType =
        requestType?.charAt(0) +
        requestType?.slice(1, requestType.length).toLowerCase();

    const hasError = responseList.some(response => response.errorStatus);
    if (hasError) {
      // might not need to do anything here.
      dispatch(
        showFeedback({
          id: Date.now(),
          message: `Dictionary ${requestType} Failed`,
          dismissable: true,
          className: 'feedback-working'
        }) as any
      );
    } else if (
      responseList.every(
        res =>
          res.requestStatus === commonEnums.DictionaryRequestStatus.COMPLETED
      )
    ) {
      if (requestType !== 'Delete') {
        const soleResponse = responseList[0];
        // redirect to the right spot.
        if (soleResponse.dictionaryId !== null) {
          isNewDesign && dispatch(loading(true) as any);
          dispatch(
            selectedDictionary({ dictionaryId: soleResponse.dictionaryId })
          );
          isNewDesign && dispatch(loading(true) as any);
          const dictionary = await api<CMxAPI.Dictionary>({
            endpoint: `/dictionary/${soleResponse.dictionaryId}/filters/v2?pageSize=0`,
            init: {
              method: HttpMethods.POST
            },
            body: []
          }).finally(() => {
            isNewDesign && dispatch(loading(false) as any);
          });
          isNewDesign && dispatch(loading(true) as any);
          await (dispatch(
            loadDictionary({
              data: dictionary,
              path: successRoute + `/dictionary/${soleResponse.dictionaryName}`
            }) as any
          ).finally(() => {
            isNewDesign && dispatch(loading(false) as any);
          }));
        }
      } else {
        history.replace(successRoute);

        dispatch(toggleDeleteModal(false));

        dispatch(
          showFeedback({
            id: Date.now(),
            message: `Dictionary Delete Success`,
            dismissable: true,
            className: 'feedback-working'
          }) as any
        );
      }
    }
    return responseList[0];
  }
);

const uploadDictionary = createAsyncThunk<
  CMxAPI.DictionaryRequestResponse,
  CMx.dictionaryUploadPayload,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryUpload.UPLOAD_DICTIONARY, async (payload, { dispatch }) => {
  const formData = new FormData();

  const {
    dictionaryCSV,
    successRoute,
    isCreate,
    tenantId,
    dictionaryName,
    description,
    purpose,
    crossMapIndicator,
    columnDefinitionMetadata,
    isCorrection,
    jiraTicket
  } = payload;

  const metadata = {
    tenantId,
    dictionaryName,
    description,
    purpose,
    crossMapIndicator,
    columnDefinitionMetadata,
    jiraTicket
  };

  var blob = new Blob([dictionaryCSV], { type: 'text/csv' });

  formData.append('dictionaryCSV', blob);
  formData.append('metadata', JSON.stringify(metadata));

  let endpoint = `/dictionary/csv`;
  if (!isCreate) {
    if (isCorrection) {
      endpoint += `/correct`;
    } else {
      endpoint += `/update`;
    }
  }
  endpoint += `/v2`;
  isNewDesign && dispatch(loading(true) as any);
  const res: CMxAPI.DictionaryRequestResponse = await api<
    CMxAPI.DictionaryRequestResponse
  >(
    {
      endpoint,
      init: {
        method: HttpMethods.POST,
        body: formData
      }
    },
    false
  )
    .catch(e => {
      if (e instanceof BadRequestError) {
        dispatch(dismissFeedback(false) as any);
        dispatch(
          showFeedbackError({
            id: Date.now(),
            message: `Dictionary Upload Unsuccessful`,
            dismissable: true
          }) as any
        );
      }
      throw e;
    })
    .finally(() => {
      isNewDesign && dispatch(loading(false) as any);
    });

  let navParams = new URLSearchParams({
    requestId: res.id.toString()
  });

  // redirect to the request uploading page
  history.push(successRoute + `/request?${navParams.toString()}`);
  return res;
});

export {
  uploadDictionary,
  selectedDictionary,
  requestInitiated,
  fetchRequestStatus,
  requestUpdate
};
